import React, { useState, useRef, useEffect } from 'react';
import {
  getDutchHolidays,
  isWeekend,
  isHoliday,
  getDayName,
  formatDate
} from '../utils/holidays';
import { uploadOvBonnetje, downloadReceipt, deleteReceipt } from '../utils/declaratiesS3';

const getWeekNumber = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

function Agenda({ month, year, userId, registrations, setRegistrations, customDistances, setCustomDistances, zakelijkeReizen, setZakelijkeReizen, ovBedragen = {}, setOvBedragen = () => {}, ovBonnetjes = {}, setOvBonnetjes = () => {}, remarks, setRemarks, userSettings = {}, agendaFilters = { verbergWeekenden: false, verbergFeestdagen: false, verbergVrij: false }, setAgendaFilters = () => {} }) {
  const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
  const holidays = getDutchHolidays(year);
  const [fullscreen, setFullscreen] = useState(false);
  const [editingDistance, setEditingDistance] = useState(null);
  const [distanceInput, setDistanceInput] = useState('');
  const [editingOvBedrag, setEditingOvBedrag] = useState(null);
  const [ovBedragInput, setOvBedragInput] = useState('');
  const [addingZakelijk, setAddingZakelijk] = useState(null);
  const [newZakelijkType, setNewZakelijkType] = useState('auto');
  const [newZakelijkKm, setNewZakelijkKm] = useState('');
  const [newZakelijkBedrag, setNewZakelijkBedrag] = useState('');
  const [editingRemark, setEditingRemark] = useState(null);
  const [remarkInput, setRemarkInput] = useState('');
  const [saved, setSaved] = useState(false);
  const [ovUploadBezig, setOvUploadBezig] = useState(null);
  const [ovPreviewUrl, setOvPreviewUrl] = useState(null);
  const [ovPreviewNaam, setOvPreviewNaam] = useState('');
  const ovFileInputRef = useRef();
  const ovFileInputDate = useRef(null);
  const { verbergWeekenden, verbergFeestdagen, verbergVrij } = agendaFilters;
  const setVerbergWeekenden = (v) => setAgendaFilters(prev => ({ ...prev, verbergWeekenden: typeof v === 'function' ? v(prev.verbergWeekenden) : v }));
  const setVerbergFeestdagen = (v) => setAgendaFilters(prev => ({ ...prev, verbergFeestdagen: typeof v === 'function' ? v(prev.verbergFeestdagen) : v }));
  const setVerbergVrij = (v) => setAgendaFilters(prev => ({ ...prev, verbergVrij: typeof v === 'function' ? v(prev.verbergVrij) : v }));
  const saveTimer = useRef(null);
  const todayRef = useRef(null);

  const today = new Date();
  const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const todayInCurrentMonth = today.getMonth() === month && today.getFullYear() === year;

  useEffect(() => {
    if (todayInCurrentMonth && todayRef.current) {
      todayRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [month, year]);

  const triggerSaved = () => {
    setSaved(true);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => setSaved(false), 2000);
  };

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days = [];

  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(Date.UTC(year, month, day));
    const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const isWeekendDay = isWeekend(date);
    const isHolidayDay = isHoliday(date, year);
    const werkdagen = userSettings.werkdagen ?? [1, 2, 3, 4, 5];
    const isWerkdag = werkdagen.includes(date.getDay());
    const dayNum = date.getDay();
    const customDefault = (userSettings.defaultRegistratieDagen || {})[dayNum];
    const defaultDag = (!isWeekendDay && !isHolidayDay && isWerkdag)
      ? (customDefault !== undefined ? customDefault : ((userSettings.defaultThuiswerkDagen || []).includes(dayNum) ? 'thuis' : ''))
      : '';
    const isAutoVrij = isWeekendDay || isHolidayDay || !isWerkdag;
    const isExplicit = registrations[monthKey]?.[dateString] !== undefined;
    const registration = registrations[monthKey]?.[dateString] ?? (isAutoVrij ? 'vrij' : defaultDag);

    days.push({
      date,
      dateString,
      dayName: getDayName(date),
      formattedDate: formatDate(date),
      weekNumber: getWeekNumber(date),
      isWeekend: isWeekendDay,
      isHoliday: isHolidayDay,
      isWerkdag,
      isExplicit,
      holidayName: holidays[dateString],
      registration,
      isToday: dateString === todayString,
      isDisabled: false
    });
  }

  const handleRegistrationChange = (dateString, value) => {
    setRegistrations(prev => ({
      ...prev,
      [monthKey]: { ...prev[monthKey], [dateString]: value }
    }));
    if (value === 'ov' && !(userSettings.heeftNsBusinesscard ?? true) && parseFloat(userSettings.ovBedragEnkelReis) > 0) {
      setOvBedragen(prev => ({
        ...prev,
        [monthKey]: { ...prev[monthKey], [dateString]: parseFloat(userSettings.ovBedragEnkelReis) * 2 }
      }));
    }
    triggerSaved();
  };

  const handleDistanceEdit = (dateString, currentKm) => {
    setEditingDistance(dateString);
    setDistanceInput(currentKm.toString());
  };

  const handleDistanceSave = (dateString) => {
    const km = parseFloat(distanceInput);
    if (isNaN(km) || km < 0) { alert('Voer een geldig aantal km in'); return; }
    setCustomDistances(prev => ({
      ...prev,
      [monthKey]: { ...prev[monthKey], [dateString]: km }
    }));
    setEditingDistance(null);
    setDistanceInput('');
    triggerSaved();
  };

  const handleDistanceCancel = () => { setEditingDistance(null); setDistanceInput(''); };

  const handleOvBedragEdit = (dateString) => {
    setEditingOvBedrag(dateString);
    setOvBedragInput((ovBedragen?.[monthKey]?.[dateString] || '').toString());
  };

  const handleOvBedragSave = (dateString) => {
    const bedrag = parseFloat(ovBedragInput.replace(',', '.'));
    if (isNaN(bedrag) || bedrag < 0) { alert('Voer een geldig bedrag in'); return; }
    setOvBedragen(prev => ({ ...prev, [monthKey]: { ...prev[monthKey], [dateString]: bedrag } }));
    setEditingOvBedrag(null);
    setOvBedragInput('');
    triggerSaved();
  };

  const handleOvBedragCancel = () => { setEditingOvBedrag(null); setOvBedragInput(''); };

  const getZakelijkeRitten = (dateString) => {
    const val = (zakelijkeReizen || {})[monthKey]?.[dateString];
    if (!val) return [];
    if (Array.isArray(val)) return val;
    return [{ id: 'legacy', km: val }];
  };

  const handleZakelijkAdd = (dateString) => {
    let nieuw;
    if (newZakelijkType === 'ov') {
      const bedrag = parseFloat(newZakelijkBedrag.replace(',', '.'));
      if (isNaN(bedrag) || bedrag <= 0) { alert('Voer een geldig bedrag in'); return; }
      nieuw = { id: Date.now().toString(), type: 'ov', bedrag };
    } else {
      const km = parseFloat(newZakelijkKm);
      if (isNaN(km) || km <= 0) { alert('Voer een geldig aantal km in'); return; }
      nieuw = { id: Date.now().toString(), type: 'auto', km };
    }
    setZakelijkeReizen(prev => {
      const huidig = getZakelijkeRitten(dateString);
      return { ...prev, [monthKey]: { ...prev[monthKey], [dateString]: [...huidig, nieuw] } };
    });
    setNewZakelijkKm('');
    setNewZakelijkBedrag('');
    setNewZakelijkType('auto');
    setAddingZakelijk(null);
    triggerSaved();
  };

  const handleZakelijkDelete = (dateString, ritId) => {
    setZakelijkeReizen(prev => {
      const huidig = getZakelijkeRitten(dateString);
      const updated = huidig.filter(r => r.id !== ritId);
      const newMonth = { ...prev[monthKey] };
      if (updated.length === 0) delete newMonth[dateString];
      else newMonth[dateString] = updated;
      return { ...prev, [monthKey]: newMonth };
    });
    triggerSaved();
  };

  const handleRemarkEdit = (dateString) => {
    setEditingRemark(dateString);
    setRemarkInput(remarks[monthKey]?.[dateString] || '');
  };

  const handleRemarkSave = (dateString) => {
    setRemarks(prev => ({
      ...prev,
      [monthKey]: { ...prev[monthKey], [dateString]: remarkInput.trim() }
    }));
    setEditingRemark(null);
    setRemarkInput('');
    triggerSaved();
  };

  const handleRemarkCancel = () => { setEditingRemark(null); setRemarkInput(''); };

  const handleRemarkDelete = (dateString) => {
    setRemarks(prev => {
      const updated = { ...prev[monthKey] };
      delete updated[dateString];
      return { ...prev, [monthKey]: updated };
    });
    triggerSaved();
  };

  const handleOvBonnetjeUpload = async (file, dateString) => {
    if (!file) return;
    setOvUploadBezig(dateString);
    try {
      const key = await uploadOvBonnetje(userId, year, month, dateString, file);
      setOvBonnetjes(prev => ({ ...prev, [monthKey]: { ...prev[monthKey], [dateString]: { key, naam: file.name, type: file.type } } }));
      triggerSaved();
    } catch (err) {
      alert('Uploaden mislukt: ' + err.message);
    } finally {
      setOvUploadBezig(null);
    }
  };

  const handleOvBonnetjeVerwijder = async (dateString) => {
    const bonnetje = ovBonnetjes?.[monthKey]?.[dateString];
    if (!bonnetje) return;
    try {
      await deleteReceipt(bonnetje.key);
      setOvBonnetjes(prev => { const u = { ...prev[monthKey] }; delete u[dateString]; return { ...prev, [monthKey]: u }; });
      triggerSaved();
    } catch (err) {
      alert('Verwijderen mislukt: ' + err.message);
    }
  };

  const handleOvBonnetjeBekijken = async (dateString) => {
    const bonnetje = ovBonnetjes?.[monthKey]?.[dateString];
    if (!bonnetje) return;
    try {
      const blob = await downloadReceipt(bonnetje.key);
      setOvPreviewUrl(URL.createObjectURL(blob));
      setOvPreviewNaam(bonnetje.naam);
    } catch (err) {
      alert('Bonnetje kon niet worden geopend: ' + err.message);
    }
  };

  const handleClearRow = (dateString) => {
    setRegistrations(prev => { const u = { ...(prev[monthKey] || {}) }; delete u[dateString]; return { ...prev, [monthKey]: u }; });
    setCustomDistances(prev => { const u = { ...(prev[monthKey] || {}) }; delete u[dateString]; return { ...prev, [monthKey]: u }; });
    setOvBedragen(prev => { const u = { ...(prev[monthKey] || {}) }; delete u[dateString]; return { ...prev, [monthKey]: u }; });
    setOvBonnetjes(prev => { const u = { ...(prev[monthKey] || {}) }; delete u[dateString]; return { ...prev, [monthKey]: u }; });
    setZakelijkeReizen(prev => { const u = { ...(prev[monthKey] || {}) }; delete u[dateString]; return { ...prev, [monthKey]: u }; });
    setRemarks(prev => { const u = { ...(prev[monthKey] || {}) }; delete u[dateString]; return { ...prev, [monthKey]: u }; });
    triggerSaved();
  };

  const handleClearAll = () => {
    setRegistrations(prev => ({ ...prev, [monthKey]: {} }));
    setCustomDistances(prev => ({ ...prev, [monthKey]: {} }));
    setOvBedragen(prev => ({ ...prev, [monthKey]: {} }));
    setOvBonnetjes(prev => ({ ...prev, [monthKey]: {} }));
    setZakelijkeReizen(prev => ({ ...prev, [monthKey]: {} }));
    setRemarks(prev => ({ ...prev, [monthKey]: {} }));
    triggerSaved();
  };

  const getEnkeleKm = (dateString, registration) => {
    if (registration === 'fiets') return customDistances[monthKey]?.[dateString] ?? (parseFloat(userSettings.kmPerRitFiets) || 0);
    if (registration === 'auto') return customDistances[monthKey]?.[dateString] ?? (parseFloat(userSettings.kmPerRitAuto) || 0);
    return 0;
  };

  const getRowClass = (day) => {
    if (day.isToday) return 'bg-blue-100 ring-2 ring-inset ring-blue-500';
    if (day.isWeekend && !day.isHoliday) return 'bg-gray-50';
    if (day.isHoliday) return day.isWeekend ? 'bg-orange-50' : 'bg-blue-50';
    return 'hover:bg-gray-50';
  };

  const werkdagenDays = days.filter(d => !d.isWeekend && !d.isHoliday && d.isWerkdag);
  const ingevuldDays = werkdagenDays.filter(d => d.registration !== '');
  const totalWerkdagen = werkdagenDays.length;
  const totalIngevuld = ingevuldDays.length;
  const volledigIngevuld = totalWerkdagen > 0 && totalIngevuld === totalWerkdagen;

  const zichtbareDagen = days.filter(day => {
    if (verbergWeekenden && day.isWeekend) return false;
    if (verbergFeestdagen && day.isHoliday) return false;
    if (verbergVrij && !day.isWerkdag && !day.isWeekend && !day.isHoliday && !day.isExplicit) return false;
    return true;
  });

  const FilterToggle = ({ actief, onClick, label }) => (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
        actief
          ? 'bg-blue-600 border-blue-600 text-white'
          : 'bg-white border-gray-300 text-gray-600 hover:border-blue-400'
      }`}
    >
      <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 ${actief ? 'bg-white border-white' : 'border-gray-400'}`}>
        {actief && <svg className="w-2.5 h-2.5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
      </span>
      {label}
    </button>
  );

  return (
    <div
      className={`bg-white flex flex-col ${fullscreen ? 'fixed inset-0 z-50 rounded-none shadow-2xl' : 'rounded-lg shadow-md'}`}
      style={fullscreen ? {} : { height: '100%' }}
    >
      <input
        ref={ovFileInputRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={e => { if (e.target.files[0] && ovFileInputDate.current) { handleOvBonnetjeUpload(e.target.files[0], ovFileInputDate.current); e.target.value = ''; } }}
      />
      {ovPreviewUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 px-4" onClick={() => { URL.revokeObjectURL(ovPreviewUrl); setOvPreviewUrl(null); }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-screen overflow-auto p-4" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-3">
              <p className="text-sm font-medium text-gray-700 truncate">{ovPreviewNaam}</p>
              <div className="flex gap-2">
                <a href={ovPreviewUrl} download={ovPreviewNaam} className="text-sm text-blue-600 hover:text-blue-800 font-medium px-3 py-1.5 rounded-lg border border-blue-200 hover:bg-blue-50 transition">Download</a>
                <button onClick={() => { URL.revokeObjectURL(ovPreviewUrl); setOvPreviewUrl(null); }} className="text-gray-400 hover:text-gray-600 px-2">✕</button>
              </div>
            </div>
            {ovPreviewNaam.toLowerCase().endsWith('.pdf')
              ? <iframe src={ovPreviewUrl} title="bonnetje" className="w-full h-96 rounded-lg border" />
              : <img src={ovPreviewUrl} alt="bonnetje" className="w-full rounded-lg" />
            }
          </div>
        </div>
      )}
      <div className="flex-shrink-0 rounded-t-lg overflow-hidden">
        <div className="bg-gradient-to-r from-blue-700 to-blue-800 px-6 py-8 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white">
            Registratie — {new Date(year, month).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })}
          </h2>
          <div className="flex items-center gap-3">
            <span className={`text-sm font-medium px-3 py-1 rounded-full bg-green-400 text-white transition-opacity duration-500 ${saved ? 'opacity-100' : 'opacity-0'}`}>
              Opgeslagen
            </span>
            <button
              type="button"
              onClick={() => setFullscreen(f => !f)}
              title={fullscreen ? 'Verklein' : 'Vergroot naar volledig scherm'}
              className="p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/20 transition"
            >
              {fullscreen ? (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 9V4.5M9 9H4.5M15 9h4.5M15 9V4.5M9 15H4.5M9 15v4.5M15 15v4.5M15 15h4.5" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                </svg>
              )}
            </button>
          </div>
        </div>

      <div className="bg-white px-6 py-3 border-b border-gray-200 flex items-center gap-3 flex-wrap">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Verbergen:</span>
        <FilterToggle actief={verbergWeekenden} onClick={() => setVerbergWeekenden(v => !v)} label="Weekenden" />
        <FilterToggle actief={verbergFeestdagen} onClick={() => setVerbergFeestdagen(v => !v)} label="Feestdagen" />
        <FilterToggle actief={verbergVrij} onClick={() => setVerbergVrij(v => !v)} label="Niet werkdagen" />
        <button
          type="button"
          onClick={() => { if (window.confirm('Weet je zeker dat je de volledige registratie van deze maand wilt leegmaken?')) handleClearAll(); }}
          title="Volledige registratie leegmaken"
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-300 bg-white text-gray-500 hover:border-red-400 hover:text-red-500 transition"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
          Volledige registratie leegmaken
        </button>
      </div>

      <div className="bg-white px-6 py-3 border-b border-gray-100 flex items-center gap-3">
        {volledigIngevuld ? (
          <span className="flex items-center gap-1.5 text-sm font-semibold text-green-700">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            Volledig ingevuld
          </span>
        ) : (
          <span className="text-sm text-gray-600">
            <span className="font-semibold">{totalIngevuld}</span>
            <span className="text-gray-400"> / {totalWerkdagen} werkdagen ingevuld</span>
          </span>
        )}
        {!volledigIngevuld && totalWerkdagen > 0 && (
          <div className="flex-1 bg-gray-200 rounded-full h-1.5 max-w-xs">
            <div
              className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${(totalIngevuld / totalWerkdagen) * 100}%` }}
            />
          </div>
        )}
      </div>

      </div>

      <div className="flex-1 min-h-0 overflow-auto">
        <table className="w-full">
          <thead>
            <tr>
              {['Dag','Week','Type','Registratie','Woon-werkverkeer','Zakelijke reizen','Opmerking',''].map((label, i) => (
                <th
                  key={i}
                  className={`sticky top-0 z-10 bg-gray-100 border-b border-gray-200 py-4 text-left text-sm font-semibold text-gray-900 ${i === 0 ? 'px-6' : i === 1 ? 'px-4' : i === 7 ? 'px-2 w-8' : 'px-6'}`}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {zichtbareDagen.map((day) => {
              const hasData = day.isExplicit
                || !!(customDistances[monthKey]?.[day.dateString])
                || !!(ovBedragen?.[monthKey]?.[day.dateString])
                || getZakelijkeRitten(day.dateString).length > 0
                || !!(remarks[monthKey]?.[day.dateString]);
              return (
                <React.Fragment key={day.dateString}>
                <tr
                  ref={day.isToday ? todayRef : null}
                  className={`group ${getRowClass(day)} transition`}
                >
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${day.isToday ? 'text-blue-700 font-bold' : 'text-gray-900'}`}>
                    <div className="flex items-center gap-2">
                      {day.isToday && (
                        <span className="text-orange-500" style={{ fontSize: '10px' }}>▶</span>
                      )}
                      <span>{day.dayName.charAt(0).toUpperCase() + day.dayName.slice(1)} {day.formattedDate}</span>
                      {day.isToday && <span className="text-xs bg-blue-500 text-white px-1.5 py-0.5 rounded-full">vandaag</span>}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-gray-400">
                    {day.weekNumber}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {day.isWeekend && !day.isHoliday ? (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-200 text-gray-800">Weekend</span>
                    ) : day.isHoliday ? (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-orange-200 text-orange-800">
                        {day.holidayName || 'Feestdag'}
                      </span>
                    ) : !day.isWerkdag ? (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">Niet-werkdag</span>
                    ) : (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">Werkdag</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={day.registration}
                      onChange={(e) => handleRegistrationChange(day.dateString, e.target.value)}
                      disabled={day.isDisabled}
                      className={`px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium transition focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        day.isDisabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white cursor-pointer'
                      }`}
                    >
                      <option value="">— —</option>
                      <option value="ov">OV</option>
                      <option value="vrij">Vrij</option>
                      <option value="fiets">Fiets</option>
                      <option value="auto">Auto</option>
                      <option value="thuis">Thuis</option>
                      <option value="ziek">Ziek</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {(day.registration === 'fiets' || day.registration === 'auto') ? (
                      editingDistance === day.dateString ? (
                        <div className="flex items-center space-x-2">
                          <div>
                            <input
                              type="number"
                              step="0.1"
                              value={distanceInput}
                              onChange={(e) => setDistanceInput(e.target.value)}
                              className="w-24 px-2 py-1 rounded-lg border border-blue-300 focus:ring-2 focus:ring-blue-500 text-sm"
                              autoFocus
                            />
                            <span className="block text-xs text-gray-400 mt-0.5">enkele km</span>
                          </div>
                          <button onClick={() => handleDistanceSave(day.dateString)} className="text-green-600 hover:text-green-800 font-bold">✓</button>
                          <button onClick={handleDistanceCancel} className="text-red-600 hover:text-red-800 font-bold">✕</button>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-3">
                          <div>
                            <span className="font-medium">{(getEnkeleKm(day.dateString, day.registration) * 2).toFixed(2).replace('.', ',')} km</span>
                            <span className="block text-xs text-gray-400">enkel: {getEnkeleKm(day.dateString, day.registration).toFixed(2).replace('.', ',')} km</span>
                          </div>
                          <button onClick={() => handleDistanceEdit(day.dateString, getEnkeleKm(day.dateString, day.registration))} className="text-blue-600 hover:text-blue-800 font-semibold">✎</button>
                        </div>
                      )
                    ) : (day.registration === 'ov' && !(userSettings.heeftNsBusinesscard ?? true)) ? (
                      <div className="flex items-center gap-3">
                        {editingOvBedrag === day.dateString ? (
                          <div className="flex items-center space-x-2">
                            <div>
                              <div className="flex items-center gap-1">
                                <span className="text-sm">€</span>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={ovBedragInput}
                                  onChange={(e) => setOvBedragInput(e.target.value)}
                                  onKeyDown={(e) => { if (e.key === 'Enter') handleOvBedragSave(day.dateString); if (e.key === 'Escape') handleOvBedragCancel(); }}
                                  className="w-24 px-2 py-1 rounded-lg border border-green-300 focus:ring-2 focus:ring-green-500 text-sm"
                                  autoFocus
                                  placeholder="0.00"
                                />
                              </div>
                              <span className="block text-xs text-gray-400 mt-0.5">OV bedrag</span>
                            </div>
                            <button onClick={() => handleOvBedragSave(day.dateString)} className="text-green-600 hover:text-green-800 font-bold">✓</button>
                            <button onClick={handleOvBedragCancel} className="text-red-600 hover:text-red-800 font-bold">✕</button>
                          </div>
                        ) : ovBedragen?.[monthKey]?.[day.dateString] ? (
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-green-700">€ {(ovBedragen[monthKey][day.dateString]).toFixed(2).replace('.', ',')}</span>
                            <button onClick={() => handleOvBedragEdit(day.dateString)} className="text-blue-600 hover:text-blue-800 font-semibold">✎</button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <span className="text-sm text-gray-500">€</span>
                            <input
                              type="number"
                              step="0.01"
                              className="w-24 px-2 py-1 rounded-lg border border-green-200 bg-green-50 focus:ring-2 focus:ring-green-500 text-sm"
                              placeholder="0.00"
                              onFocus={(e) => { setEditingOvBedrag(day.dateString); setOvBedragInput(e.target.value); }}
                              readOnly
                            />
                          </div>
                        )}
                        <div className="flex items-center gap-1 border-l border-gray-200 pl-3">
                          {ovUploadBezig === day.dateString ? (
                            <svg className="w-4 h-4 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                            </svg>
                          ) : ovBonnetjes?.[monthKey]?.[day.dateString] ? (
                            <>
                              <button onClick={() => handleOvBonnetjeBekijken(day.dateString)} title={ovBonnetjes[monthKey][day.dateString].naam}
                                className="flex items-center gap-1 text-xs text-green-700 font-medium hover:text-green-900 transition"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                </svg>
                                bonnetje
                              </button>
                              <button onClick={() => handleOvBonnetjeVerwijder(day.dateString)} className="text-gray-300 hover:text-red-500 transition ml-1" title="Bonnetje verwijderen">✕</button>
                            </>
                          ) : (
                            <button
                              onClick={() => { ovFileInputDate.current = day.dateString; ovFileInputRef.current.click(); }}
                              className="flex items-center gap-1 text-xs text-gray-400 hover:text-blue-600 transition"
                              title="Bonnetje uploaden"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                              </svg>
                              bonnetje
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {day.isWeekend || day.isHoliday ? (
                      <span className="text-gray-400">—</span>
                    ) : (
                      <div className="space-y-1">
                        {getZakelijkeRitten(day.dateString).map(rit => (
                          <div key={rit.id} className="flex items-center gap-2">
                            {rit.type === 'ov'
                              ? <span className="font-medium text-purple-700">OV € {(rit.bedrag || 0).toFixed(2).replace('.', ',')}</span>
                              : <span className="font-medium text-purple-700">{(rit.km || 0).toFixed(2).replace('.', ',')} km</span>
                            }
                            <button
                              onClick={() => handleZakelijkDelete(day.dateString, rit.id)}
                              className="text-red-400 hover:text-red-600 font-bold text-xs"
                            >✕</button>
                          </div>
                        ))}
                        {addingZakelijk === day.dateString ? (
                          <div className="space-y-1">
                            {!(userSettings.heeftNsBusinesscard ?? true) && (
                              <div className="flex items-center gap-2">
                                <select
                                  value={newZakelijkType}
                                  onChange={(e) => setNewZakelijkType(e.target.value)}
                                  className="px-2 py-1 rounded-lg border border-purple-300 text-xs"
                                >
                                  <option value="auto">Auto/Fiets (km)</option>
                                  <option value="ov">OV (bedrag)</option>
                                </select>
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              {newZakelijkType === 'ov' && !(userSettings.heeftNsBusinesscard ?? true) ? (
                                <>
                                  <span className="text-xs text-gray-400">€</span>
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={newZakelijkBedrag}
                                    onChange={(e) => setNewZakelijkBedrag(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleZakelijkAdd(day.dateString); if (e.key === 'Escape') { setAddingZakelijk(null); setNewZakelijkBedrag(''); setNewZakelijkType('auto'); } }}
                                    className="w-20 px-2 py-1 rounded-lg border border-purple-300 focus:ring-2 focus:ring-purple-500 text-sm"
                                    autoFocus
                                    placeholder="0.00"
                                  />
                                </>
                              ) : (
                                <>
                                  <input
                                    type="number"
                                    step="0.1"
                                    value={newZakelijkKm}
                                    onChange={(e) => setNewZakelijkKm(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleZakelijkAdd(day.dateString); if (e.key === 'Escape') { setAddingZakelijk(null); setNewZakelijkKm(''); setNewZakelijkType('auto'); } }}
                                    className="w-20 px-2 py-1 rounded-lg border border-purple-300 focus:ring-2 focus:ring-purple-500 text-sm"
                                    autoFocus
                                    placeholder="0.0"
                                  />
                                  <span className="text-xs text-gray-400">km</span>
                                </>
                              )}
                              <button onClick={() => handleZakelijkAdd(day.dateString)} className="text-green-600 hover:text-green-800 font-bold">✓</button>
                              <button onClick={() => { setAddingZakelijk(null); setNewZakelijkKm(''); setNewZakelijkBedrag(''); setNewZakelijkType('auto'); }} className="text-red-600 hover:text-red-800 font-bold">✕</button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => setAddingZakelijk(day.dateString)}
                            className="text-gray-400 hover:text-purple-600 text-xs"
                          >+ rit</button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {editingRemark === day.dateString ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={remarkInput}
                          onChange={(e) => setRemarkInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleRemarkSave(day.dateString); if (e.key === 'Escape') handleRemarkCancel(); }}
                          className="w-48 px-2 py-1 rounded-lg border border-blue-300 focus:ring-2 focus:ring-blue-500 text-sm"
                          autoFocus
                          placeholder="Opmerking..."
                        />
                        <button onClick={() => handleRemarkSave(day.dateString)} className="text-green-600 hover:text-green-800 font-bold">✓</button>
                        <button onClick={handleRemarkCancel} className="text-red-600 hover:text-red-800 font-bold">✕</button>
                      </div>
                    ) : remarks[monthKey]?.[day.dateString] ? (
                      <div className="flex items-center space-x-2">
                        <span>{remarks[monthKey][day.dateString]}</span>
                        <button onClick={() => handleRemarkEdit(day.dateString)} className="text-blue-600 hover:text-blue-800">✎</button>
                        <button onClick={() => handleRemarkDelete(day.dateString)} className="text-red-400 hover:text-red-600">✕</button>
                      </div>
                    ) : (
                      <button onClick={() => handleRemarkEdit(day.dateString)} className="text-gray-400 hover:text-blue-600 text-xs">+ opmerking</button>
                    )}
                  </td>
                  <td className="px-2 py-4 text-right">
                    {hasData && (
                      <button
                        type="button"
                        onClick={() => handleClearRow(day.dateString)}
                        title="Registratie leegmaken"
                        className="opacity-0 group-hover:opacity-100 transition text-gray-300 hover:text-red-500"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </td>
                </tr>
                <tr className="group" style={{ borderTopWidth: 0 }}>
                  <td colSpan={8} className="px-6 py-0">
                    <div className="max-h-0 group-hover:max-h-8 overflow-hidden transition-all duration-150">
                      <button
                        type="button"
                        onClick={() => setAddingZakelijk(day.dateString)}
                        className="text-xs text-gray-400 hover:text-purple-600 flex items-center gap-1 py-1"
                      >
                        <span className="font-bold">+</span>
                        <span>voeg extra zakelijke reis toe voor deze dag</span>
                      </button>
                    </div>
                  </td>
                </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Agenda;
